import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import numeral from 'numeral';
import { getTone, getFinancierTone } from '../../../theme/tones';

// Helvetica (police standard du PDF) ne connaît pas l'espace fine insécable
// que produit Intl en fr-FR : on formate à la main, « 40 314,00 € ».
const formatEuro = (value) => `${numeral(Number(value) || 0).format('0,0.00').replace(/,/g, ' ').replace('.', ',')} €`;
const formatShare = (value) => `${(Number(value) || 0).toFixed(1).replace('.', ',')} %`;

const BRAND = getTone('brand').base;
const NET = getTone('emerald').deep;
const MUTED = '#64748b';
const BORDER = '#e2e8f0';
const STRIPE = '#f8fafc';

const styles = StyleSheet.create({
  page: {
    paddingVertical: 32,
    paddingHorizontal: 36,
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#1e293b'
  },
  banner: {
    backgroundColor: BRAND,
    borderRadius: 4,
    paddingVertical: 14,
    paddingHorizontal: 18,
    marginBottom: 18
  },
  bannerTitle: {
    color: '#ffffff',
    fontFamily: 'Helvetica-Bold',
    fontSize: 15
  },
  bannerSubtitle: {
    color: '#ffffff',
    fontSize: 9,
    marginTop: 4,
    opacity: 0.85
  },
  sectionTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    color: BRAND,
    marginBottom: 8,
    marginTop: 6,
    paddingLeft: 6,
    borderLeftWidth: 3,
    borderLeftColor: BRAND
  },
  tiles: {
    flexDirection: 'row',
    marginBottom: 16
  },
  tile: {
    flex: 1,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 4,
    padding: 10
  },
  tileGap: {
    width: 10
  },
  tileLabel: {
    fontSize: 8,
    color: MUTED,
    textTransform: 'uppercase'
  },
  tileValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 14,
    marginTop: 4
  },
  tileHint: {
    fontSize: 8,
    color: MUTED,
    marginTop: 2
  },
  table: {
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 4,
    marginBottom: 16
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    alignItems: 'center'
  },
  rowLast: {
    borderBottomWidth: 0
  },
  headerRow: {
    backgroundColor: BRAND
  },
  headerCell: {
    color: '#ffffff',
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    paddingVertical: 6,
    paddingHorizontal: 6
  },
  cell: {
    paddingVertical: 5,
    paddingHorizontal: 6
  },
  totalRow: {
    backgroundColor: '#f1f5f9'
  },
  bold: {
    fontFamily: 'Helvetica-Bold'
  },
  right: {
    textAlign: 'right'
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 5
  },
  note: {
    fontSize: 8,
    color: MUTED,
    marginTop: -8,
    marginBottom: 12
  },
  footer: {
    position: 'absolute',
    bottom: 16,
    left: 36,
    right: 36,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 7,
    color: MUTED
  }
});

const FINANCIER_COLUMNS = [
  { key: 'name', label: 'Financeur', width: '28%' },
  { key: 'share', label: 'Part du net', width: '12%', right: true },
  { key: 'count', label: 'Inscriptions net / brut', width: '22%', right: true },
  { key: 'ca_net', label: 'CA net', width: '19%', right: true },
  { key: 'ca_brut', label: 'CA brut', width: '19%', right: true }
];

const COMMERCIAL_COLUMNS = [
  { key: 'name', label: 'Commercial', width: '30%' },
  { key: 'count_net', label: 'Ventes', width: '11%', right: true },
  { key: 'ca_net', label: 'CA net', width: '19%', right: true },
  { key: 'count_brut', label: 'Inscriptions', width: '15%', right: true },
  { key: 'ca_brut', label: 'CA brut', width: '25%', right: true }
];

const HeaderRow = ({ columns }) => (
  <View style={[styles.row, styles.headerRow]}>
    {columns.map((column) => (
      <Text
        key={column.key}
        style={[styles.headerCell, { width: column.width }, column.right ? styles.right : null]}
      >
        {column.label}
      </Text>
    ))}
  </View>
);

const Cell = ({ width, right, children, style }) => (
  <Text style={[styles.cell, { width }, right ? styles.right : null, style]}>
    {children}
  </Text>
);

/**
 * Rapport PDF du CA d'une période (mois ou année) : KPIs, répartition par financeur,
 * puis le CA de chaque commercial sur le même mois.
 */
const MonthlyRevenueReportPDF = ({ periodLabel, generatedAt, total, financiers, commercials, system = null }) => {
  // Seuls les commerciaux qui ont vendu sur le mois figurent dans le rapport :
  // CA net à 0 € ou aucune inscription nette = ligne retirée.
  const hasSales = (row) => Number(row.ca_net) > 0 && Number(row.count_net) > 0;
  const activeCommercials = commercials.filter(hasSales);
  // Inscriptions sans origine connue (source 997 / vide) : même règle d'affichage.
  const systemRow = system && hasSales(system) ? system : null;
  const commercialsTotal = [...activeCommercials, ...(systemRow ? [systemRow] : [])].reduce((acc, row) => ({
    ca_net: acc.ca_net + (Number(row.ca_net) || 0),
    ca_brut: acc.ca_brut + (Number(row.ca_brut) || 0),
    count_net: acc.count_net + (Number(row.count_net) || 0),
    count_brut: acc.count_brut + (Number(row.count_brut) || 0)
  }), { ca_net: 0, ca_brut: 0, count_net: 0, count_brut: 0 });
  // Le CA du mois compte toutes les inscriptions ; celui des commerciaux
  // seulement celles qui leur sont rattachées. L'écart est expliqué sous le tableau.
  const unassignedNet = Number(total.ca_net) - commercialsTotal.ca_net;

  return (
    <Document title={`CA - ${periodLabel}`}>
      <Page
        size="A4"
        style={styles.page}
      >
        <View style={styles.banner}>
          <Text style={styles.bannerTitle}>{`Chiffre d'affaires — ${periodLabel}`}</Text>
          <Text style={styles.bannerSubtitle}>{`Généré le ${generatedAt}`}</Text>
        </View>

        {/* KPIs DU MOIS */}
        <Text style={styles.sectionTitle}>Indicateurs de la période</Text>
        <View style={styles.tiles}>
          <View style={styles.tile}>
            <Text style={styles.tileLabel}>CA net</Text>
            <Text style={[styles.tileValue, { color: NET }]}>{formatEuro(total.ca_net)}</Text>
            <Text style={styles.tileHint}>{`${total.count_net} inscriptions (Inscrit + InscritD)`}</Text>
          </View>
          <View style={styles.tileGap} />
          <View style={styles.tile}>
            <Text style={styles.tileLabel}>CA brut</Text>
            <Text style={styles.tileValue}>{formatEuro(total.ca_brut)}</Text>
            <Text style={styles.tileHint}>{`${total.count_brut} inscriptions, tous statuts`}</Text>
          </View>
          <View style={styles.tileGap} />
          <View style={styles.tile}>
            <Text style={styles.tileLabel}>Écart brut - net</Text>
            <Text style={styles.tileValue}>{formatEuro(Number(total.ca_brut) - Number(total.ca_net))}</Text>
            <Text style={styles.tileHint}>{`${total.count_brut - total.count_net} inscriptions non retenues`}</Text>
          </View>
        </View>

        {/* PAR FINANCEUR */}
        <Text style={styles.sectionTitle}>CA par financeur</Text>
        <View style={styles.table}>
          <HeaderRow columns={FINANCIER_COLUMNS} />
          {financiers.length === 0 && (
            <View style={[styles.row, styles.rowLast]}>
              <Cell
                width="100%"
                style={{ color: MUTED }}
              >
                Aucune inscription sur cette période.
              </Cell>
            </View>
          )}
          {financiers.map((financier, index) => {
            const share = Number(total.ca_net) > 0 ? (Number(financier.ca_net) / Number(total.ca_net)) * 100 : 0;
            return (
              <View
                key={financier.name}
                style={[styles.row, index % 2 === 1 ? { backgroundColor: STRIPE } : null]}
                wrap={false}
              >
                <View style={[styles.cell, { width: '28%', flexDirection: 'row', alignItems: 'center' }]}>
                  <View style={[styles.dot, { backgroundColor: getTone(getFinancierTone(financier.name)).base }]} />
                  <Text>{financier.name}</Text>
                </View>
                <Cell
                  width="12%"
                  right
                >
                  {formatShare(share)}
                </Cell>
                <Cell
                  width="22%"
                  right
                >
                  {`${financier.count_net} / ${financier.count_brut}`}
                </Cell>
                <Cell
                  width="19%"
                  right
                  style={[styles.bold, { color: NET }]}
                >
                  {formatEuro(financier.ca_net)}
                </Cell>
                <Cell
                  width="19%"
                  right
                >
                  {formatEuro(financier.ca_brut)}
                </Cell>
              </View>
            );
          })}
          {financiers.length > 0 && (
            <View
              style={[styles.row, styles.rowLast, styles.totalRow]}
              wrap={false}
            >
              <Cell
                width="28%"
                style={styles.bold}
              >
                Total
              </Cell>
              <Cell
                width="12%"
                right
                style={styles.bold}
              >
                {formatShare(Number(total.ca_net) > 0 ? 100 : 0)}
              </Cell>
              <Cell
                width="22%"
                right
                style={styles.bold}
              >
                {`${total.count_net} / ${total.count_brut}`}
              </Cell>
              <Cell
                width="19%"
                right
                style={[styles.bold, { color: NET }]}
              >
                {formatEuro(total.ca_net)}
              </Cell>
              <Cell
                width="19%"
                right
                style={styles.bold}
              >
                {formatEuro(total.ca_brut)}
              </Cell>
            </View>
          )}
        </View>

        {/* PAR COMMERCIAL */}
        <Text
          style={styles.sectionTitle}
          minPresenceAhead={60}
        >
          CA par commercial
        </Text>
        <View style={styles.table}>
          <HeaderRow columns={COMMERCIAL_COLUMNS} />
          {activeCommercials.length === 0 && !systemRow && (
            <View style={styles.row}>
              <Cell
                width="100%"
                style={{ color: MUTED }}
              >
                Aucune vente commerciale sur cette période.
              </Cell>
            </View>
          )}
          {activeCommercials.map((row, index) => (
            <View
              key={row.id}
              style={[styles.row, index % 2 === 1 ? { backgroundColor: STRIPE } : null]}
              wrap={false}
            >
              <View style={[styles.cell, { width: '30%' }]}>
                <Text style={styles.bold}>{`${row.firstname || ''} ${row.lastname || ''}`.trim() || 'Commercial'}</Text>
                {row.email || Number(row.is_external) === 1 ? (
                  <Text style={{ fontSize: 7, color: MUTED, marginTop: 1 }}>
                    {[Number(row.is_external) === 1 ? 'Prestataire externe' : null, row.email].filter(Boolean).join(' · ')}
                  </Text>
                ) : null}
              </View>
              <Cell
                width="11%"
                right
              >
                {row.count_net}
              </Cell>
              <Cell
                width="19%"
                right
                style={[styles.bold, { color: NET }]}
              >
                {formatEuro(row.ca_net)}
              </Cell>
              <Cell
                width="15%"
                right
              >
                {row.count_brut}
              </Cell>
              <Cell
                width="25%"
                right
              >
                {formatEuro(row.ca_brut)}
              </Cell>
            </View>
          ))}
          {systemRow && (
            <View
              style={[styles.row, activeCommercials.length % 2 === 1 ? { backgroundColor: STRIPE } : null]}
              wrap={false}
            >
              <View style={[styles.cell, { width: '30%' }]}>
                <Text style={styles.bold}>Système / Pas connu</Text>
                <Text style={{ fontSize: 7, color: MUTED, marginTop: 1 }}>Inscriptions sans commercial d&apos;origine</Text>
              </View>
              <Cell
                width="11%"
                right
              >
                {systemRow.count_net}
              </Cell>
              <Cell
                width="19%"
                right
                style={[styles.bold, { color: NET }]}
              >
                {formatEuro(systemRow.ca_net)}
              </Cell>
              <Cell
                width="15%"
                right
              >
                {systemRow.count_brut}
              </Cell>
              <Cell
                width="25%"
                right
              >
                {formatEuro(systemRow.ca_brut)}
              </Cell>
            </View>
          )}
          <View
            style={[styles.row, styles.rowLast, styles.totalRow]}
            wrap={false}
          >
            <Cell
              width="30%"
              style={styles.bold}
            >
              {systemRow ? `Total (${activeCommercials.length} vendeurs + système)` : `Total (${activeCommercials.length} vendeurs)`}
            </Cell>
            <Cell
              width="11%"
              right
              style={styles.bold}
            >
              {commercialsTotal.count_net}
            </Cell>
            <Cell
              width="19%"
              right
              style={[styles.bold, { color: NET }]}
            >
              {formatEuro(commercialsTotal.ca_net)}
            </Cell>
            <Cell
              width="15%"
              right
              style={styles.bold}
            >
              {commercialsTotal.count_brut}
            </Cell>
            <Cell
              width="25%"
              right
              style={styles.bold}
            >
              {formatEuro(commercialsTotal.ca_brut)}
            </Cell>
          </View>
        </View>
        {Math.abs(unassignedNet) >= 0.01 && (
          <Text style={styles.note}>
            {`${formatEuro(unassignedNet)} de CA net de la période viennent d'inscriptions saisies par d'autres utilisateurs (service Suivi, administrateurs…) : comptés dans les indicateurs de la période, ils n'apparaissent pas dans ce tableau.`}
          </Text>
        )}

        <View
          style={styles.footer}
          fixed
        >
          <Text>{`Chiffre d'affaires — ${periodLabel}`}</Text>
          <Text>{`Généré le ${generatedAt}`}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default MonthlyRevenueReportPDF;
